import React, { useState, useEffect } from 'react';
import { X, Check, Sparkles, Users, Zap, Loader2, ShieldCheck, CreditCard } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';

/**
 * MyAccountModal (UpgradePlansModal) — Plan comparison, upgrade, billing, and account info.
 *
 * Tabs:
 *   Plans   — tier comparison, Upgrade (Stripe checkout), Manage Billing (Stripe portal)
 *   Account — email, account created, last login, last login IP
 */
function UpgradePlansModal({
  isOpen,
  onClose,
  currentTier = 'free',
  user = null,
  onBillingPortal,
  billingPortalLoading = false,
  defaultTab = 'plans'
}) {
  const { tierLimits } = useAuth();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showNudgeForm, setShowNudgeForm] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Sync defaultTab when modal opens
  useEffect(() => {
    if (isOpen) setActiveTab(defaultTab);
  }, [isOpen, defaultTab]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Tier limits with fallbacks
  const freeCredits = tierLimits?.free?.credits_per_month || 50;
  const freeDatabases = tierLimits?.free?.max_databases || 2;
  const proCredits = tierLimits?.pro?.credits_per_month || 1000;
  const proDatabases = tierLimits?.pro?.max_databases || 10;
  const teamCredits = tierLimits?.team?.credits_per_month || 5000;
  const teamDatabases = tierLimits?.team?.max_databases || 100;

  const creditMultiplier = freeCredits > 0 ? Math.round(proCredits / freeCredits) : 5;
  const databaseMultiplier = freeDatabases > 0 ? Math.round(proDatabases / freeDatabases) : 5;

  const isPaidTier = currentTier === 'pro' || currentTier === 'team';

  const plans = [
    {
      name: 'Free',
      tier: 'free',
      icon: Sparkles,
      price: '$0',
      priceSubtext: 'Forever free',
      description: 'Perfect for trying out VerbaGPT',
      features: [
        `${freeCredits} queries per month`,
        `Up to ${freeDatabases} databases`,
        'Virgo + Taurus access',
        'File uploads (CSV, Excel, JSON, and more)',
        '14-day chat history',
        'Shareable chat links'
      ],
      popular: false
    },
    {
      name: 'Pro',
      tier: 'pro',
      icon: Zap,
      price: '$20/mo',
      priceSubtext: 'Early adopter rate — lock in forever',
      description: 'For power users and data professionals',
      features: [
        'Everything in Free, plus:',
        `${proCredits} queries per month (${creditMultiplier}x more)`,
        `Up to ${proDatabases} databases (${databaseMultiplier}x more)`,
        'Unlimited chat history',
        'Larger file uploads',
        'Taurus Projects (create & save workflows)',
        'Priority support'
      ],
      popular: true,
      badge: 'Most Popular'
    },
    {
      name: 'Team',
      tier: 'team',
      icon: Users,
      price: 'Coming Soon',
      priceSubtext: 'Pricing TBD',
      description: 'For teams and enterprises',
      features: [
        'Everything in Pro',
        'Unlimited queries per month',
        'Unlimited database connections',
        'Admin panel & org management',
        'Shared datasources & data notes',
        'Role-based access control',
        'Prompt libraries',
        'SSO & advanced security'
      ],
      popular: false
    }
  ];

  const getButtonConfig = (planTier) => {
    const tierHierarchy = { free: 0, pro: 1, team: 2 };
    const planLevel = tierHierarchy[planTier] || 0;
    const currentLevel = tierHierarchy[currentTier] || 0;

    if (planLevel === currentLevel) {
      // Current plan — show Manage Billing for paid tiers
      if (isPaidTier) {
        return {
          text: 'Manage Billing',
          disabled: billingPortalLoading,
          action: 'billing',
          className: 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
        };
      }
      return {
        text: 'Current Plan',
        disabled: true,
        action: null,
        className: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 cursor-not-allowed'
      };
    } else if (planLevel > currentLevel) {
      return {
        text: planTier === 'team' ? 'Contact Us' : 'Upgrade',
        disabled: false,
        action: 'upgrade',
        className: 'bg-blue-600 hover:bg-blue-700 text-white'
      };
    } else {
      return {
        text: 'Downgrade',
        disabled: billingPortalLoading,
        action: 'downgrade',
        className: 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
      };
    }
  };

  const handleUpgradeClick = async () => {
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_ENDPOINTS.main}/stripe/create-checkout-session`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const handleNudgeClick = (tier) => {
    setSelectedTier(tier);
    setShowNudgeForm(true);
    setMessage('');
    setSubmitted(false);
    setError(null);
  };

  const handleSubmitNudge = async () => {
    if (!selectedTier) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_ENDPOINTS.main}/feedback/submit`,
        {
          feedback_type: 'upgrade_interest',
          message: message.trim() || null,
          metadata: { requested_tier: selectedTier }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubmitted(true);
      setTimeout(() => {
        setShowNudgeForm(false);
        setSelectedTier(null);
        setMessage('');
        setSubmitted(false);
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowNudgeForm(false);
    setSelectedTier(null);
    setMessage('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Account</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage your plan, billing, and account details
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0 px-4">
          {['plans', 'account'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab === 'plans' ? 'Plans & Billing' : 'Account Details'}
            </button>
          ))}
        </div>

        {/* Plans Tab */}
        {activeTab === 'plans' && (
          <div className="p-6 overflow-y-auto flex-1">
            {error && !showNudgeForm && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const Icon = plan.icon;
                const isCurrentPlan = currentTier === plan.tier;
                const buttonConfig = getButtonConfig(plan.tier);

                return (
                  <div
                    key={plan.tier}
                    className={`relative rounded-lg border-2 p-4 flex flex-col ${
                      plan.popular
                        ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    } ${isCurrentPlan ? 'ring-2 ring-green-500 ring-offset-2 dark:ring-offset-gray-900' : ''}`}
                  >
                    {plan.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-600 text-white">
                          {plan.badge}
                        </span>
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 right-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-600 text-white">
                          Current Plan
                        </span>
                      </div>
                    )}

                    {/* Icon & Name */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-1.5 rounded-lg ${
                        plan.popular
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                    </div>

                    {/* Price */}
                    <div className="mb-3">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{plan.price}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{plan.priceSubtext}</div>
                    </div>

                    {/* Description */}
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">{plan.description}</p>

                    {/* Features */}
                    <ul className="space-y-2 mb-4 flex-1">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-1.5">
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-gray-700 dark:text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <div>
                      <button
                        onClick={() => {
                          if (buttonConfig.action === 'billing') onBillingPortal?.();
                          else if (buttonConfig.action === 'downgrade') onBillingPortal?.();
                          else if (buttonConfig.action === 'upgrade') {
                            if (plan.tier === 'pro') handleUpgradeClick();
                            else handleNudgeClick(plan.tier);
                          }
                        }}
                        disabled={buttonConfig.disabled || checkoutLoading}
                        className={`w-full py-2 px-4 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2 ${buttonConfig.className}`}
                      >
                        {checkoutLoading && plan.tier === 'pro' ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting...</>
                        ) : billingPortalLoading && (buttonConfig.action === 'billing' || buttonConfig.action === 'downgrade') ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Loading...</>
                        ) : buttonConfig.action === 'billing' ? (
                          <><CreditCard className="h-4 w-4" /> {buttonConfig.text}</>
                        ) : buttonConfig.text}
                      </button>
                      {buttonConfig.action === 'downgrade' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                          To downgrade, cancel your subscription in the billing portal. Your Pro access continues until the end of the billing period.
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="p-6 overflow-y-auto flex-1">
            <div className="max-w-lg">
              <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Account Security
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Email</span>
                    <span className="text-gray-900 dark:text-white font-medium">{user?.email || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Plan</span>
                    <span className="text-gray-900 dark:text-white font-medium capitalize">{currentTier}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Account created</span>
                    <span className="text-gray-900 dark:text-white">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Last login</span>
                    <span className="text-gray-900 dark:text-white">
                      {user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : '—'}
                    </span>
                  </div>
                  {user?.last_login_ip && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Last login IP</span>
                      <span className="text-gray-500 dark:text-gray-400 font-mono text-xs">{user.last_login_ip}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nudge Form Overlay */}
        {showNudgeForm && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center p-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
              {!submitted ? (
                <>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Interested in {plans.find(p => p.tier === selectedTier)?.name}?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Let us know you're interested! We'll notify you when pricing and features are finalized.
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message (Optional)
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Tell us what features you're most excited about..."
                      rows={4}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowNudgeForm(false)}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitNudge}
                      disabled={submitting}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</> : 'Submit'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Thanks for your interest!</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    We'll notify you when {plans.find(p => p.tier === selectedTier)?.name} launches.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default UpgradePlansModal;
